import React from 'react';
import { FileText, Download, Image as ImageIcon, Film, Music, FileArchive } from 'lucide-react';
import { cn } from '../../lib/utils';

const FileAttachment = ({ attachment, showPreview = true }) => {
    // Support both url/file_url and size/file_size for backward compatibility
    const { filename, url, file_url, file_type, size, file_size } = attachment;
    const fileUrl = file_url || url;
    const fileSize = file_size || size;

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
    };

    const getFileIcon = () => {
        switch (file_type) {
            case 'image':
                return <ImageIcon className="w-5 h-5" />;
            case 'video':
                return <Film className="w-5 h-5" />;
            case 'audio':
                return <Music className="w-5 h-5" />;
            case 'pdf':
                return <FileText className="w-5 h-5 text-red-500" />;
            default:
                return <FileText className="w-5 h-5" />;
        }
    };

    // Image preview
    if (file_type === 'image' && showPreview) {
        return (
            <div className="mt-2 max-w-sm">
                <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                    <img
                        src={fileUrl}
                        alt={filename}
                        className="rounded-lg border border-gray-200 hover:opacity-90 transition-opacity cursor-pointer max-h-80 object-contain"
                        loading="lazy"
                    />
                </a>
                <p className="text-xs text-gray-500 mt-1 truncate">{filename}</p>
            </div>
        );
    }

    // Video preview
    if (file_type === 'video' && showPreview) {
        return (
            <div className="mt-2 max-w-md">
                <video
                    src={fileUrl}
                    controls
                    className="rounded-lg border border-gray-200 w-full max-h-80"
                    preload="metadata"
                >
                    Your browser does not support the video tag.
                </video>
                <p className="text-xs text-gray-500 mt-1 truncate">{filename}</p>
            </div>
        );
    }

    // Document/File card
    return (
        <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors max-w-sm"
        >
            <div className="text-gray-600">{getFileIcon()}</div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{filename}</p>
                <p className="text-xs text-gray-500">{formatFileSize(fileSize)}</p>
            </div>
            <Download className="w-4 h-4 text-gray-400 flex-shrink-0" />
        </a>
    );
};

export default FileAttachment;
